import { Popover, PopoverContent, PopoverTrigger } from '@ibsys/kit';
import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { RouteConfig } from '@/shared/lib';
import { SidebarFlyoutItem, SidebarFlyoutLink } from './SidebarFlyoutLink';
import { SidebarFlyoutRouteLinks } from './SidebarFlyoutRouteLinks';

type Props = {
  /** Заголовок меню — название раздела/пункта */
  title: ReactNode;
  /** Пункты группы — каждый вместе с подключёнными к нему динамическими пунктами */
  routes?: RouteConfig[];
  /** Пункты подменю после пунктов группы; если нет ни их, ни `routes` — показывается только заголовок */
  items?: SidebarFlyoutItem[];
  children: ReactNode;
};

const CLOSE_DELAY_MS = 350;

/**
 * Всплывающее меню при наведении на свёрнутую иконку корневого пункта сайдбара:
 * сверху — заголовок (название раздела), ниже, если это группа — список её пунктов.
 * Открывается по hover/focus и закрывается с небольшой задержкой, чтобы курсор успевал
 * переместиться с иконки на содержимое меню (иначе оно закрывалось бы раньше клика).
 */
export const SidebarFlyoutMenu: FC<Props> = ({ title, routes, items, children }) => {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  const handleOpen = () => {
    clearTimeout(closeTimer.current);
    setOpen(true);
  };

  const handleClose = () => {
    closeTimer.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  };

  const handleNavigate = () => setOpen(false);

  return (
    <Popover open={open} onOpenChange={setOpen} trigger={'hover'}>
      <PopoverTrigger asChild>
        <div onFocus={handleOpen} onBlur={handleClose}>
          {children}
        </div>
      </PopoverTrigger>
      <PopoverContent
        side={'right'}
        align={'start'}
        sideOffset={8}
        className={'p-0 w-56'}
        onMouseEnter={handleOpen}
        onMouseLeave={handleClose}
      >
        <div className={'px-3 py-2 border-b border-border text-sm font-medium text-foreground'}>{title}</div>
        {(!!routes?.length || !!items?.length) && (
          <div className={'flex flex-col p-1'}>
            {routes?.map((route) => (
              <SidebarFlyoutRouteLinks
                key={route.navbar?.path || route.path}
                route={route}
                onNavigate={handleNavigate}
              />
            ))}
            {items?.map((item) => (
              <SidebarFlyoutLink key={item.path} item={item} onNavigate={handleNavigate} />
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
